import styles from './index.module.css'

import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import api from '@disco/api/server'
import { RESTART_SUBSCRIPTION } from '@disco/constants/ActionTypes'
import {
  loadBusinessData,
  loadBusinessSubscription,
} from '@disco/state-management/actions/UserActions'
import {
  getIsExpiredSubscription,
  getSubscriptionPeriod,
  getSubscriptionScheduledCancellationAt,
} from '@disco/state-management/selectors/subscription'
import {
  getConstanceSettings,
  getCorePlan,
  getUser,
} from '@disco/state-management/selectors/user'
import captureError, { parseServerError } from '@disco/utils/captureError'
import { ENTERPRISE_ID } from '@disco/utils/featuresAccessLevel'
import { pushUINotification } from '@disco/utils/notifications'

import type { SubscriptionData } from 'types/subscription'

import CardNumber from 'components/common/card-number'
import { Box, Flex, Text } from 'components/ds'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import Card from 'components/ui/card'
import ContactUsLink from 'components/ui/contact-us-link'
import DiscoModal from 'components/ui/disco-modal'
import Heading from 'components/ui/heading'
import Icon from 'components/ui/icon'
import List from 'components/ui/list'
import ListItemWithIcon from 'components/ui/list-item-with-icon'
import modalStyles from 'components/utils/modalStyles'

import useChargebeePricingPage from 'hooks/use-chargebee-pricing-page'
import { useDispatch, useSelector, useStore } from 'hooks/use-redux'

import type { AppDispatch, AppStore } from 'store/configureStore'

import ExpiredSubscriptionModal from './modal'

// Poll for the backend to asynchronously clear `expired_subscription` post-restart.
export const REACTIVATION_POLL_ATTEMPTS = 10
export const REACTIVATION_POLL_INTERVAL_MS = 1000
// Give the success toast a beat to register before navigating away.
const REACTIVATION_SUCCESS_NAVIGATE_DELAY_MS = 2000

// Resolves after `ms`, or early if `signal` aborts.
const delay = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise(resolve => {
    if (signal?.aborted) return resolve()
    const timer = setTimeout(resolve, ms)
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer)
        resolve()
      },
      { once: true },
    )
  })

// Refetches until both expired flag and scheduled cancellation clear; true if cleared, false on timeout/abort.
export const pollForReactivation = async (
  dispatch: AppDispatch,
  store: AppStore,
  signal?: AbortSignal,
): Promise<boolean> => {
  for (let attempt = 0; attempt < REACTIVATION_POLL_ATTEMPTS; attempt++) {
    // Wait before each fetch so the backend has a beat to propagate the change.
    await delay(REACTIVATION_POLL_INTERVAL_MS, signal)
    if (signal?.aborted) return false

    try {
      await Promise.all([
        dispatch(loadBusinessData()),
        dispatch(loadBusinessSubscription()),
      ])
    } catch (error) {
      // Transient fetch error — log and retry on the next attempt.
      captureError(error, {
        moduleName: 'ExpiredSubscription',
        description: 'Failed to refresh data while polling for reactivation',
      })
      continue
    }
    if (signal?.aborted) return false

    const state = store.getState()
    const stillExpired = getIsExpiredSubscription(state)
    const stillScheduledForCancellation =
      getSubscriptionScheduledCancellationAt(state)
    if (!stillExpired && !stillScheduledForCancellation) {
      return true
    }
  }
  return false
}

const formatFiles = files => {
  if (files === Infinity) {
    return 'Unlimited'
  } else if (files > 1000) {
    return `${Math.floor(files / 1000)}K`
  } else {
    return files
  }
}

type Props = {
  users: number
  files: number
  showUpdatePaymentDetails: boolean
  disabledUpdatePaymentDetails: boolean
  subscription: SubscriptionData
  planName: string
  showConfirmModal: boolean
  totalDue: string | null
  fetchEstimateError?: boolean
  onRetryFetchEstimate: () => void
  // Signals a reactivation is in flight so the container suppresses its redirect.
  onRestartingChange?: (isRestarting: boolean) => void
}

const ExpiredSubscription = ({
  users,
  files,
  showUpdatePaymentDetails,
  disabledUpdatePaymentDetails,
  subscription,
  planName,
  showConfirmModal,
  totalDue,
  fetchEstimateError,
  onRetryFetchEstimate,
  onRestartingChange,
}: Props): React.ReactNode => {
  const [loading, setLoading] = useState(false)
  const [cardDeclined, setCardDeclined] = useState(false)
  const dispatch = useDispatch()
  const store = useStore()
  const navigate = useNavigate()
  // Aborts in-flight polling/navigation if the modal unmounts.
  const abortControllerRef = useRef<AbortController | null>(null)
  useEffect(() => () => abortControllerRef.current?.abort(), [])
  // TODO: merge duplication between props.subscription and getSubscriptionPeriod reading from redux state.subscription
  const period = useSelector(getSubscriptionPeriod)
  const constance = useSelector(getConstanceSettings)
  const userData = useSelector(getUser)
  const isEnterprisePlan = useSelector(getCorePlan)?.id === ENTERPRISE_ID
  const wasLegacyPlan =
    userData.business?.private_metadata?.bulk_switched_from_legacy_plan
  const cardDeclinedMessage =
    'Your card was declined. Please update your billing information below.'

  const isGrowthRestart = Boolean(
    constance['CB-59-GROWTH-AT-RESTART-SUBSCRIPTION'],
  )
  // Enterprise restarts are negotiated by CS, so they must not reach Chargebee self-serve.
  const routeToSupport = isGrowthRestart && isEnterprisePlan

  const { url: growthLink, hasFailed: growthLinkFailed } =
    useChargebeePricingPage('/', isGrowthRestart && !routeToSupport)

  if (showConfirmModal) {
    if (isGrowthRestart) {
      // Without a pricing page link the restart button would be a dead end.
      return routeToSupport || growthLinkFailed ? (
        <ExpiredSubscriptionModal contactSupport />
      ) : (
        <ExpiredSubscriptionModal href={growthLink} />
      )
    }
    return (
      <ExpiredSubscriptionModal
        onClose={() => {
          dispatch({
            type: RESTART_SUBSCRIPTION,
          })
        }}
      />
    )
  }

  const reSubscription = async () => {
    // Cancel any in-flight attempt so a double-click can't run two concurrent flows.
    abortControllerRef.current?.abort()
    const abortController = new AbortController()
    abortControllerRef.current = abortController
    const { signal } = abortController
    setLoading(true)
    // Hold the container's "not expired → go home" redirect while we navigate ourselves.
    onRestartingChange?.(true)

    try {
      await api.chargebeeRestartSubscription()

      const reactivated = await pollForReactivation(dispatch, store, signal)
      if (signal.aborted) return

      if (reactivated) {
        pushUINotification({
          message: `Subscription successfully reactivated!`,
          level: 'success',
        })
        // Give the success toast a beat before navigating away.
        await delay(REACTIVATION_SUCCESS_NAVIGATE_DELAY_MS, signal)
        if (signal.aborted) return
        navigate('/settings/billing')
        return
      }

      // Restart succeeded but the flag hasn't propagated yet — keep the modal open.
      pushUINotification({
        message:
          'Your subscription is being reactivated and may take a moment to finish. Please refresh shortly, or contact support if this persists.',
        level: 'warning',
      })
    } catch (error) {
      if (signal.aborted) return

      const errorMessage = parseServerError(error.body)

      if (errorMessage === 'The payment method on file could not be charged.') {
        setCardDeclined(true)
        pushUINotification({
          message: cardDeclinedMessage,
          level: 'error',
        })
      } else {
        pushUINotification({
          message: errorMessage,
          level: 'error',
        })
      }
    }

    // Reached only on timeout or error (the success path navigates and returns above).
    if (!signal.aborted) {
      setLoading(false)
      // Always release the restart hold so isRestarting can't get stuck true.
      onRestartingChange?.(false)
      // On a partial-clear timeout the container redirect won't fire, so navigate ourselves.
      if (!getIsExpiredSubscription(store.getState())) {
        navigate('/')
      }
    }
  }

  return (
    <DiscoModal
      isOpen
      style={{
        ...modalStyles,
        content: {
          ...modalStyles.content,
          backgroundColor: '#f5f6f8',
          boxShadow: '0px 3px 5px #091e4233, 0px 0px 1px #091e424f',
          borderRadius: '3px',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: 0,
        },
      }}
    >
      <div className={styles.expiredSubscription}>
        <Heading size="700">Restart Subscription</Heading>
        {cardDeclined && (
          <div className={styles.expiredSubscriptionAlertContainer}>
            <Alert status="danger" icon="warning">
              {cardDeclinedMessage}
            </Alert>
          </div>
        )}

        <Card
          key={1}
          title={`You’re re-activating to the ${planName} Plan`}
          footerLinks={[
            {
              linkNode: (
                <Button
                  compact
                  appearance="inline-link"
                  caption={
                    <ContactUsLink
                      className={styles.expiredSubscriptionLink}
                      intercomMsg="I’d like to re-subscribe and change my plan"
                    >
                      Change plan
                    </ContactUsLink>
                  }
                />
              ),
            },
          ]}
        >
          {wasLegacyPlan && (
            <div className={styles.expiredSubscriptionAlert}>
              <Alert
                status="warning"
                icon="warning"
                dismissible
                id="reactivating_legacy_plan"
              >
                You were previously on legacy plan, which is no longer offered.
                You can reactivate to our {planName} plan, or check out our
                other plans via link below.
                <br />
                <a
                  className="text-link"
                  href="https://disco.ac/pricing"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View current plans
                </a>
              </Alert>
            </div>
          )}
          <List>
            <ListItemWithIcon
              iconNode={<Icon type="profiles" color="#5f63ee" />}
            >
              With {users} users
            </ListItemWithIcon>
            <ListItemWithIcon iconNode={<Icon type="lyrics" color="#FFAB00" />}>
              {formatFiles(files)} Tracks
            </ListItemWithIcon>
            {(subscription.loading || period) && (
              <ListItemWithIcon
                iconNode={<Icon type="calendar" color="#6B778C" />}
              >
                {subscription.loading && <span>loading...</span>}
                {period === 'annual' && <span>Annual billing</span>}
                {period === 'monthly' && <span>Monthly billing</span>}
              </ListItemWithIcon>
            )}
          </List>
        </Card>
        {showUpdatePaymentDetails && (
          <>
            <Heading size="700">Billing Details</Heading>
            <Card
              key={2}
              title="Credit Card information"
              footerLinks={[
                {
                  linkNode: (
                    <Button
                      compact
                      appearance="inline-link"
                      caption="Update Billing info"
                      link="/settings/billing/update"
                      disabled={disabledUpdatePaymentDetails}
                      tooltip={
                        disabledUpdatePaymentDetails
                          ? 'Only admins on your DISCO can update card information.'
                          : undefined
                      }
                    />
                  ),
                },
              ]}
            >
              <CardNumber />
            </Card>
          </>
        )}
      </div>

      <div className={styles.expiredSubscriptionFooter}>
        <Flex direction="column" gap="2" flexGrow="1">
          <Flex mb="2">
            {fetchEstimateError ? (
              <Box mr="2">
                <Text size="2" as="div">
                  We‘re having trouble loading your pricing summary.
                </Text>
                <Text size="2" as="div">
                  Try again or{' '}
                  <ContactUsLink intercomMsg="I need help restarting my subscription">
                    contact support
                  </ContactUsLink>
                </Text>
              </Box>
            ) : (
              <div className={styles.expiredSubscriptionFooterDescription}>
                Total Due: {totalDue ?? <span>loading...</span>}
              </div>
            )}
          </Flex>
          <Flex>
            {fetchEstimateError ? (
              <Button
                caption="Try again"
                appearance="primary"
                onClick={onRetryFetchEstimate}
                fullWidth
              />
            ) : (
              <Button
                progressIndicator={loading || totalDue === null}
                progressCaption="Loading"
                caption="Restart Subscription"
                appearance="primary"
                onClick={reSubscription}
                disabled={loading || totalDue === null}
                fullWidth
              />
            )}
          </Flex>
        </Flex>
      </div>
    </DiscoModal>
  )
}

export default ExpiredSubscription
